'use client';

import { RoomEvent, Track } from 'livekit-client';
import * as React from 'react';
import {
  CarouselLayout,
  FocusLayoutContainer,
  GridLayout,
  isTrackReference,
  LayoutContextProvider,
  RoomAudioRenderer,
  useCreateLayoutContext,
  usePinnedTracks,
  useTracks,
  type MessageDecoder,
  type MessageEncoder,
  type MessageFormatter,
  type TrackReferenceOrPlaceholder,
  type WidgetState,
} from '@livekit/components-react';
import { AgimaChat } from './AgimaChat';
import { AssistantPanel } from './AssistantPanel';
import { AgimaConnectionToast } from './AgimaConnectionToast';
import { AgimaControlBar } from './AgimaControlBar';
import { AgimaParticipantTile } from './AgimaParticipantTile';
import { KeyboardShortcuts } from '@/lib/KeyboardShortcuts';
import { RaiseHandChime } from './RaiseHandChime';
import { SelfVideoCollapseContext } from './SelfVideoCollapseContext';
import { ru } from '@/lib/ru';

function CollapsedSelfVideo(props: { name: string }) {
  const { toggle } = React.useContext(SelfVideoCollapseContext);
  return (
    <div className="agima-collapsed-self-video" aria-label={ru.collapsedSelfVideo}>
      <span className="agima-collapsed-self-video-name">{props.name}</span>
      <button
        type="button"
        className="agima-self-video-expand"
        aria-label={ru.expandSelfVideo}
        title={ru.expandSelfVideo}
        onClick={toggle}
      >
        ↗
      </button>
    </div>
  );
}

function isEqualTrackRef(
  a?: TrackReferenceOrPlaceholder,
  b?: TrackReferenceOrPlaceholder,
): boolean {
  if (a === undefined || b === undefined) return false;
  if (isTrackReference(a) && isTrackReference(b)) {
    return a.publication.trackSid === b.publication.trackSid;
  }
  return a.participant.identity === b.participant.identity && a.source === b.source;
}

export function AgimaVideoConference(props: {
  chatMessageFormatter?: MessageFormatter;
  chatMessageEncoder?: MessageEncoder;
  chatMessageDecoder?: MessageDecoder;
  SettingsComponent?: React.ComponentType;
  showAssistant?: boolean;
  assistantEnabled?: boolean;
  meetingId?: string | null;
}) {
  const { chatMessageFormatter, chatMessageDecoder, chatMessageEncoder, SettingsComponent, showAssistant = false, assistantEnabled = false, meetingId = null } = props;
  // Opens by default for the host: the point of enabling the assistant is to see it.
  const [assistantOpen, setAssistantOpen] = React.useState(showAssistant && assistantEnabled);
  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
  });
  const [selfVideoCollapsed, setSelfVideoCollapsed] = React.useState(false);
  const lastAutoFocusedScreenShareTrack = React.useRef<TrackReferenceOrPlaceholder | null>(null);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );

  const layoutContext = useCreateLayoutContext();
  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);
  const localCameraTrack = tracks.find(
    (track) => track.participant.isLocal && track.source === Track.Source.Camera,
  );
  const layoutTracks = selfVideoCollapsed
    ? tracks.filter((track) => !isEqualTrackRef(track, localCameraTrack))
    : tracks;
  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const effectiveFocusTrack =
    selfVideoCollapsed &&
    focusTrack?.participant.isLocal &&
    focusTrack.source === Track.Source.Camera
      ? undefined
      : focusTrack;
  const carouselTracks = layoutTracks.filter(
    (track) => !isEqualTrackRef(track, effectiveFocusTrack),
  );

  React.useEffect(() => {
    if (
      selfVideoCollapsed &&
      focusTrack?.participant.isLocal &&
      focusTrack.source === Track.Source.Camera
    ) {
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
    }
  }, [focusTrack, layoutContext.pin, selfVideoCollapsed]);

  React.useEffect(() => {
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      console.debug('Auto set screen share focus:', { newScreenShareTrack: screenShareTracks[0] });
      layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: screenShareTracks[0] });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      console.debug('Auto clearing screen share focus.');
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: updatedFocusTrack });
      }
    }
  }, [
    screenShareTracks
      .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
      .join(),
    focusTrack?.publication?.trackSid,
    tracks,
  ]);

  return (
    <div className="lk-video-conference">
      {typeof window !== 'undefined' && (
        <LayoutContextProvider value={layoutContext} onWidgetChange={setWidgetState}>
          <KeyboardShortcuts />
          <SelfVideoCollapseContext.Provider
            value={{
              collapsed: selfVideoCollapsed,
              toggle: () => setSelfVideoCollapsed((collapsed) => !collapsed),
            }}
          >
            <div className="lk-video-conference-inner">
              {!effectiveFocusTrack ? (
                <div className="lk-grid-layout-wrapper">
                  <GridLayout tracks={layoutTracks}>
                    <AgimaParticipantTile />
                  </GridLayout>
                </div>
              ) : (
                <div className="lk-focus-layout-wrapper">
                  <FocusLayoutContainer>
                    <CarouselLayout tracks={carouselTracks}>
                      <AgimaParticipantTile />
                    </CarouselLayout>
                    {effectiveFocusTrack && <AgimaParticipantTile trackRef={effectiveFocusTrack} />}
                  </FocusLayoutContainer>
                </div>
              )}
              {localCameraTrack && selfVideoCollapsed && (
                <CollapsedSelfVideo
                  name={localCameraTrack.participant.name || localCameraTrack.participant.identity}
                />
              )}
              <AgimaControlBar
                controls={{ chat: true, settings: !!SettingsComponent, assistant: showAssistant }}
                assistantOpen={assistantOpen}
                onToggleAssistant={() => setAssistantOpen((value) => !value)}
              />
            </div>
          </SelfVideoCollapseContext.Provider>
          {showAssistant && (
            <AssistantPanel
              open={assistantOpen && !widgetState.showChat}
              onClose={() => setAssistantOpen(false)}
              meetingId={meetingId}
              assistantEnabled={assistantEnabled}
            />
          )}
          <AgimaChat
            style={{ display: widgetState.showChat ? 'grid' : 'none' }}
            messageFormatter={chatMessageFormatter}
            messageEncoder={chatMessageEncoder}
            messageDecoder={chatMessageDecoder}
          />
          {SettingsComponent && (
            <div
              className="lk-settings-menu-modal"
              style={{ display: widgetState.showSettings ? 'block' : 'none' }}
            >
              <SettingsComponent />
            </div>
          )}
        </LayoutContextProvider>
      )}
      <RoomAudioRenderer />
      <RaiseHandChime />
      <AgimaConnectionToast />
    </div>
  );
}
